import { storageKeys } from 'config/settings.json';

import APIHandler from 'config/APIHandler';
import DataHandler from 'config/DataHandler';
import StorageHandler from 'config/StorageHandler';

/**
 *
 * @param {{ app: Record<string, unknown>[]}} appInfo
 * @param {string} code language code
 * @param {number} appId app id
 */
export function UNSAFE_malformAppInfo(appInfo, code, appId) {
  const [app = undefined] = appInfo.app ?? [];

  if (app === undefined) {
    throw new Error('App info is undefined');
  }

  const {
    landingVideo,
    language_display_name: languageDisplayName,
    left_graphic: leftGraphic,
    center_graphic: centerGraphic,
    bottom_graphic: bottomGraphic,
    home_slider_images: homeSliderImages = [],
    logo,
    logo_white: logoWhite,
    logo_wide_color: logoWideColor,
    logo_wide_white: logoWideWhite,
    primary_color: primaryColor,
    secondary_color: secondaryColor,
    landing_quote: landingQuote,
    programBenefits,
    facebook,
    flickr,
    instagram,
    twitter,
    youtube,
    pinterest,
    googlePlus,
    linkedin,
    tikTok: tiktok,
    weibo,
    appName,
    appNameDevice,
    clientName,
    login_image: loginImage,
    question_header: questionHeader,
    profile_image_header: profileImageHeader,
    google_play_url: googlePlayUrl,
    apple_store_url: appleStoreUrl,
    video_email_copy: videoEmailCopy,
    video_email_subject: videoEmailSubject,
    image_email_copy: imageEmailCopy,
    image_email_subject: imageEmailSubject,
    shareTitle,
    shareCopy,
    twitter_copy: twitterCopy,
    welcome_page_copy: welcomePageCopy,
    languages,
    feature_flags: featureFlags = [],
    sales_companion_apps: salesCompanionApps,
    website_url: companyWebsite,
    client_id: clientId,
    client_secret: clientSecret,
    website_url: programUrl,
    train_learn_config: trainLearnConfig,
    clientWebsite,
    general_background_image: generalBackgroundImage,
    directory_url: directoryURL,
    custom_share_domain: customShareDomainRaw,
    introVideo,
    splashScreen,
    custom_css: customCSS,
    fast_track_app_id: fastTrackAppId,
    font,
    body_font: bodyFont,
    destination,
  } = app;

  const socials = {
    facebook: facebook !== '' ? facebook : null,
    flickr: flickr !== '' ? flickr : null,
    instagram: instagram !== '' ? instagram : null,
    twitter: twitter !== '' ? twitter : null,
    youtube: youtube !== '' ? youtube : null,
    pinterest: pinterest !== '' ? pinterest : null,
    googlePlus: googlePlus !== '' ? googlePlus : null,
    linkedin: linkedin !== '' ? linkedin : null,
    tiktok: tiktok !== '' ? tiktok : null,
    weibo: weibo !== '' ? weibo : null,
  };

  const companionDisplayNames = (salesCompanionApps || []).reduce((acc, item) => {
    const normalized = item.language.includes('_') ? item.language.replace('_', '-') : item.language;

    const corrected = normalized.includes('-')
      ? `${normalized.split('-')[0].toLowerCase()}-${normalized.split('-')[1].toUpperCase()}`
      : `${normalized.toLowerCase()}-${normalized.toUpperCase()}`;

    const display = item.language_display_name;

    if (display) acc[corrected] = display;
    return acc;
  }, {});

  const flags = featureFlags.filter((flag) => {
    return flag.is_enabled === true;
  });

  const sortedSliderImages = homeSliderImages
    .filter((slide) => {
      return Boolean(slide.image);
    })
    .sort((a, b) => {
      return a.sort_order - b.sort_order;
    })
    .map((slide) => {
      return {
        id: slide.id,
        imageUrl: slide.image.replace('s3.amazonaws.com/developertool', 'front.travpromobile.com'),
      };
    });

  const isEnGBDefault = flags.find((flag) => {
    return flag.name === 'en_gb_default_language';
  });

  const defaultEnglish = isEnGBDefault
    ? {
        code: 'en-GB',
        country: 'UK English',
        langFile: 'gb',
      }
    : {
        code: 'en-US',
        country: (languageDisplayName && languageDisplayName.trim()) || 'Global English',
        langFile: 'en',
      };

  if (!code.includes('-')) {
    StorageHandler.save(storageKeys.lang, defaultEnglish);
  }

  let currentLanguage = code ? code : defaultEnglish.code;
  const salesCompanion = salesCompanionApps
    ? salesCompanionApps.filter(({ language: lan }) => {
        return lan === currentLanguage.replace('-', '_');
      })[0]
    : null;

  const currentAppId = appId ?? app.app_id ?? import.meta.env.VITE_APP_ID;
  const salesCompanionAppId = currentLanguage.includes('en') || !salesCompanion ? currentAppId : salesCompanion.id;

  return {
    appId: currentAppId,
    backgroundImage: generalBackgroundImage
      ? generalBackgroundImage.replace('s3.amazonaws.com/developertool', 'front.travpromobile.com')
      : null,
    backgroundVideo: landingVideo
      ? landingVideo.replace('s3.amazonaws.com/developertool', 'front.travpromobile.com')
      : null,
    loginImage: loginImage ? loginImage.replace('s3.amazonaws.com/developertool', 'front.travpromobile.com') : null,
    logo: logo ? logo.replace('s3.amazonaws.com/developertool', 'front.travpromobile.com') : null,
    logoWhite: logoWhite ? logoWhite.replace('s3.amazonaws.com/developertool', 'front.travpromobile.com') : null,
    logoWide: logoWideColor ? logoWideColor.replace('s3.amazonaws.com/developertool', 'front.travpromobile.com') : null,
    logoWideWhite: logoWideWhite
      ? logoWideWhite.replace('s3.amazonaws.com/developertool', 'front.travpromobile.com')
      : null,
    leftGraphic: leftGraphic ? leftGraphic.replace('s3.amazonaws.com/developertool', 'front.travpromobile.com') : null,
    centerGraphic: centerGraphic
      ? centerGraphic.replace('s3.amazonaws.com/developertool', 'front.travpromobile.com')
      : null,
    bottomGraphic: bottomGraphic
      ? bottomGraphic.replace('s3.amazonaws.com/developertool', 'front.travpromobile.com')
      : null,
    primaryColor: primaryColor !== '' ? primaryColor : null,
    secondaryColor: secondaryColor !== '' ? secondaryColor : null,
    landingQuote: landingQuote !== '' ? landingQuote : null,
    homeSliderImages: sortedSliderImages,
    appName: appName !== '' ? appName : null,
    appNameDevice: appNameDevice !== '' ? appNameDevice : null,
    clientName: clientName !== '' ? clientName : null,
    questionHeader: questionHeader
      ? questionHeader.replace('s3.amazonaws.com/developertool', 'front.travpromobile.com')
      : null,
    profileImageHeader: profileImageHeader
      ? profileImageHeader.replace('s3.amazonaws.com/developertool', 'front.travpromobile.com')
      : null,
    introVideo: introVideo ? introVideo.replace('s3.amazonaws.com/developertool', 'front.travpromobile.com') : null,
    programBenefits,
    socials,
    appleStore: appleStoreUrl,
    googlePlay: googlePlayUrl,
    video_email_copy: videoEmailCopy,
    video_email_subject: videoEmailSubject,
    image_email_copy: imageEmailCopy,
    image_email_subject: imageEmailSubject,
    shareTitle,
    shareCopy,
    twitter_copy: twitterCopy,
    welcomePageCopy: welcomePageCopy !== '' ? welcomePageCopy : null,
    featureFlags: flags,
    salesCompanionAppId,
    companyWebsite,
    clientId,
    clientSecret,
    clientWebsite,
    programUrl,
    trainLearnConfig,
    fastTrackAppId,
    splashScreen,
    customCSS,
    font,
    bodyFont,
    destination: destination && String(destination).trim() !== '' ? String(destination).trim() : null,
    directoryURL,
    customShareDomain:
      customShareDomainRaw && String(customShareDomainRaw).trim() !== '' ? String(customShareDomainRaw).trim() : null,
    languages: (() => {
      const apiLanguages = languages
        ? languages.map((lan) => {
            const correctedLan = lan.includes('-')
              ? `${lan.split('-')[0].toLowerCase()}-${lan.split('-')[1].toUpperCase()}`
              : `${lan.toLowerCase()}-${lan.toUpperCase()}`;

            const languageFile = lan.includes('-') ? lan.split('-')[0].toLowerCase() : lan.toLowerCase();

            const names = new Intl.DisplayNames(['en'], { type: 'region' });

            const country = companionDisplayNames[correctedLan] || names.of(correctedLan.split('-')[1].toUpperCase());

            return {
              code: correctedLan,
              langFile: languageFile,
              country,
            };
          })
        : [];

      const allLanguages = [
        defaultEnglish,
        ...apiLanguages.filter((lang) => {
          return lang.code !== defaultEnglish.code;
        }),
      ];

      return allLanguages;
    })(),
  };
}

const AppSettings = async () => {
  const { code } = await StorageHandler.load(storageKeys.lang);
  const appId = await DataHandler.matchAppId();

  const lang = code ? code : '';

  const appInfo = await APIHandler.getAppInfo(appId, lang);

  if (appInfo) {
    const appInfoObject = UNSAFE_malformAppInfo(appInfo, code, appId);

    StorageHandler.save(storageKeys.appInfo, appInfoObject);

    return true;
  }
};

export default AppSettings;
